import { useMemo } from 'react';
import { isCorporateForm, shouldShowListedCompanyField } from '../constants';

/**
 * Custom hook to manage conditional field visibility logic
 * Uses memoization to prevent unnecessary recalculations and optimize performance
 *
 * @param watchedValues - Form values to watch for conditional rendering
 * @returns Object containing visibility flags for conditional fields
 *
 * @example
 * ```typescript
 * const visibility = useConditionalFields({
 *   isFinancialIntermediary: 'yes',
 *   legalForm: 'AG',
 *   isPepInvolved: 'no'
 * });
 *
 * if (visibility.showFinancialIntermediaryFields) {
 *   // Render financial intermediary fields
 * }
 * ```
 */
export const useConditionalFields = (watchedValues: {
  isFinancialIntermediary?: string;
  listedCompany?: string;
  subsidiaryOwnership?: string;
  legalForm?: string;
  isPepInvolved?: string;
  sameAddressAsContractingParty?: boolean;
}) => {
  const {
    isFinancialIntermediary,
    listedCompany,
    subsidiaryOwnership,
    legalForm,
    isPepInvolved,
    sameAddressAsContractingParty,
  } = watchedValues;

  return useMemo(
    () => ({
      showFinancialIntermediaryFields: isFinancialIntermediary === 'yes',
      showListedCompanyFields: listedCompany === 'yes',
      showParentSubsidiaryFields: subsidiaryOwnership === 'yes',
      showCorporateFields: isCorporateForm(legalForm),
      showListedCompanyField: shouldShowListedCompanyField(legalForm),
      showPepFields: isPepInvolved === 'yes',
      showOperationalOfficeFields: !sameAddressAsContractingParty,
    }),
    [
      isFinancialIntermediary,
      listedCompany,
      subsidiaryOwnership,
      legalForm,
      isPepInvolved,
      sameAddressAsContractingParty,
    ]
  );
};
